import { create } from 'zustand';
import {
  savedFiltersService,
  type SavedFilter,
  type CreateSavedFilterDto,
  type UpdateSavedFilterDto,
} from '@services/index';

interface SavedFiltersState {
  // Data
  savedFilters: SavedFilter[];
  activeFilterId: string | null;
  isModified: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchSavedFilters: () => Promise<void>;
  createSavedFilter: (dto: CreateSavedFilterDto) => Promise<SavedFilter | null>;
  updateSavedFilter: (id: string, dto: UpdateSavedFilterDto) => Promise<SavedFilter | null>;
  deleteSavedFilter: (id: string) => Promise<boolean>;
  setActiveFilter: (id: string | null) => void;
  setModified: (modified: boolean) => void;
  setDefault: (id: string) => Promise<void>;
  unsetDefault: (id: string) => Promise<void>;
  trackUsage: (id: string) => void;
  getDefaultFilter: () => SavedFilter | undefined;
  getMyFilters: (userId: string) => SavedFilter[];
  getTeamFilters: (userId: string) => SavedFilter[];
  reset: () => void;
}

export const useSavedFiltersStore = create<SavedFiltersState>()((set, get) => ({
  savedFilters: [],
  activeFilterId: null,
  isModified: false,
  isLoading: false,
  error: null,

  fetchSavedFilters: async () => {
    set({ isLoading: true, error: null });
    try {
      const savedFilters = await savedFiltersService.getAll();
      set({ savedFilters, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load saved filters',
        isLoading: false,
      });
    }
  },

  createSavedFilter: async (dto) => {
    try {
      const filter = await savedFiltersService.create(dto);
      set((state) => ({
        savedFilters: [...state.savedFilters, filter],
      }));
      return filter;
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to save filter';
      set({ error: message });
      return null;
    }
  },

  updateSavedFilter: async (id, dto) => {
    try {
      const updated = await savedFiltersService.update(id, dto);
      set((state) => ({
        savedFilters: state.savedFilters.map((f) => (f.id === id ? updated : f)),
        isModified: false,
      }));
      return updated;
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to update filter';
      set({ error: message });
      return null;
    }
  },

  deleteSavedFilter: async (id) => {
    try {
      await savedFiltersService.delete(id);
      set((state) => ({
        savedFilters: state.savedFilters.filter((f) => f.id !== id),
        activeFilterId: state.activeFilterId === id ? null : state.activeFilterId,
      }));
      return true;
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to delete filter';
      set({ error: message });
      return false;
    }
  },

  setActiveFilter: (id) => {
    set({ activeFilterId: id, isModified: false });
  },

  setModified: (modified) => {
    set({ isModified: modified });
  },

  setDefault: async (id) => {
    try {
      const updated = await savedFiltersService.setDefault(id);
      set((state) => ({
        savedFilters: state.savedFilters.map((f) => ({
          ...f,
          isDefault: f.id === id ? true : f.createdBy === updated.createdBy ? false : f.isDefault,
        })),
      }));
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to set default';
      set({ error: message });
    }
  },

  unsetDefault: async (id) => {
    try {
      // Reuse the generic update endpoint — it honours isDefault:false and
      // simply clears the flag without promoting another filter.
      await savedFiltersService.update(id, { isDefault: false });
      set((state) => ({
        savedFilters: state.savedFilters.map((f) =>
          f.id === id ? { ...f, isDefault: false } : f
        ),
      }));
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to remove default';
      set({ error: message });
    }
  },

  trackUsage: (id) => {
    // Fire and forget - don't block UI
    savedFiltersService.trackUsage(id).catch(() => {});
    set((state) => ({
      savedFilters: state.savedFilters.map((f) =>
        f.id === id ? { ...f, usageCount: f.usageCount + 1, lastUsedAt: new Date().toISOString() } : f
      ),
    }));
  },

  getDefaultFilter: () => {
    return get().savedFilters.find((f) => f.isDefault);
  },

  getMyFilters: (userId) => {
    return get().savedFilters.filter((f) => f.createdBy === userId);
  },

  getTeamFilters: (userId) => {
    return get().savedFilters.filter((f) => f.visibility === 'public' && f.createdBy !== userId);
  },

  reset: () => {
    set({
      savedFilters: [],
      activeFilterId: null,
      isModified: false,
      isLoading: false,
      error: null,
    });
  },
}));
